import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import {
  Star, Search, Sparkles, Trash2, Edit3, Eye, Plus,
  ChevronLeft, ChevronRight, Check, X, FileText, BarChart3, Bookmark,
} from 'lucide-react';

const API_BASE = 'https://api.cevee.ai';

interface Company {
  id: string;
  name: string;
  industry?: string;
  location?: string;
  looking_for?: string;
}

interface SpotlightItem {
  id: string;
  company_id: string;
  cv_id: string;
  candidate_user_id: string;
  target_role: string | null;
  summary: string;
  fit: string;
  status: string;
  position: number;
  created_at: number;
  profile?: Record<string, any>;
}

interface DiscoverCandidate {
  cv_id: string;
  explorer_id: string;
  score: number;
  current_role?: string;
  current_level?: string;
  base_locations?: string;
  preferred_locations?: string;
  role_preferences?: string;
  career_status?: string;
}

interface CVImages {
  cv_id: string;
  images: string[];
  image_count: number;
}

interface StatItem {
  company_id: string;
  company_name: string;
  total: number;
  new: number;
  seen: number;
  saved: number;
  reached_out: number;
  dismissed: number;
  queued: number;
}

const SpotlightManager: React.FC = () => {
  const { apiCall } = useAuth();

  // Company picker
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  // Active spotlights for selected company
  const [spotlights, setSpotlights] = useState<SpotlightItem[]>([]);
  const [selectedSpotlight, setSelectedSpotlight] = useState<SpotlightItem | null>(null);
  const [editTargetRole, setEditTargetRole] = useState('');
  const [editSummary, setEditSummary] = useState('');
  const [editFit, setEditFit] = useState('');

  // Discover candidates
  const [searchQuery, setSearchQuery] = useState('');
  const [candidates, setCandidates] = useState<DiscoverCandidate[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<DiscoverCandidate | null>(null);

  // CV viewer (right panel)
  const [cvImages, setCvImages] = useState<CVImages | null>(null);
  const [cvLoading, setCvLoading] = useState(false);
  const [cvPage, setCvPage] = useState(0);

  // New spotlight creation
  const [newTargetRole, setNewTargetRole] = useState('');
  const [newSummary, setNewSummary] = useState('');
  const [newFit, setNewFit] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiLanguage, setAiLanguage] = useState<'de' | 'en'>('de');
  const [jobDescription, setJobDescription] = useState('');
  const [jobSuggestions, setJobSuggestions] = useState<{title: string; location?: string}[]>([]);

  // Stats
  const [stats, setStats] = useState<StatItem[]>([]);
  const [showStats, setShowStats] = useState(false);

  // Saved CVs (left panel tab)
  const [leftTab, setLeftTab] = useState<'search' | 'saved'>('search');
  const [savedCandidates, setSavedCandidates] = useState<DiscoverCandidate[]>([]);
  const [savedLoading, setSavedLoading] = useState(false);

  // Resizable split between CV viewer and edit panel
  const [cvSplitPercent, setCvSplitPercent] = useState(65);
  const splitContainerRef = useRef<HTMLDivElement>(null);
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const container = splitContainerRef.current;
    if (!container) return;
    const onMove = (ev: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const pct = ((ev.clientY - rect.top) / rect.height) * 100;
      setCvSplitPercent(Math.min(85, Math.max(30, pct)));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: string } | null>(null);
  const showToast = (message: string, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Company search
  const [companyQuery, setCompanyQuery] = useState('');
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const companySearchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const companyPickerRef = useRef<HTMLDivElement>(null);

  const searchCompanies = useCallback(async (q: string) => {
    if (q.trim().length < 1) {
      setCompanies([]);
      return;
    }
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/companies/search-for-join?q=${encodeURIComponent(q)}&limit=20`);
      if (resp.ok) {
        const data = await resp.json();
        setCompanies(data.companies || []);
        setCompanyDropdownOpen(true);
      }
    } catch (err) {
      console.error('Company search failed:', err);
    }
  }, [apiCall]);

  const handleCompanyQueryChange = (val: string) => {
    setCompanyQuery(val);
    if (companySearchTimeout.current) clearTimeout(companySearchTimeout.current);
    companySearchTimeout.current = setTimeout(() => searchCompanies(val), 250);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (companyPickerRef.current && !companyPickerRef.current.contains(e.target as Node)) {
        setCompanyDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load spotlights when company changes
  const loadSpotlights = useCallback(async () => {
    if (!selectedCompany) return;
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/spotlights/admin/company/${selectedCompany.id}`);
      if (resp.ok) {
        const data = await resp.json();
        setSpotlights(data.spotlights || []);
      }
    } catch (err) {
      console.error('Failed to load spotlights:', err);
    }
  }, [selectedCompany, apiCall]);

  useEffect(() => {
    loadSpotlights();
    setCandidates([]);
    setSelectedCandidate(null);
    setSelectedSpotlight(null);
  }, [selectedCompany, loadSpotlights]);

  // Discover candidates
  const discoverCandidates = async (query?: string) => {
    if (!selectedCompany) return;
    setSearchLoading(true);
    try {
      const body: any = { company_id: selectedCompany.id, page_size: 20 };
      if (query?.trim()) body.query = query.trim();
      const resp = await apiCall(`${API_BASE}/api/v1/companies/discover-candidates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (resp.ok) {
        const data = await resp.json();
        setCandidates(data.candidates || []);
        const suggestions = data.reach_out_suggestions?.job_titles;
        if (Array.isArray(suggestions)) {
          const seen = new Set<string>();
          setJobSuggestions(suggestions.filter((jt: any) => {
            const key = jt.title?.toLowerCase();
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          }));
        }
      }
    } catch (err) {
      console.error('Discover failed:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  // Load on company select
  useEffect(() => {
    if (selectedCompany) discoverCandidates();
  }, [selectedCompany]);

  // Load CV images
  const loadCv = async (cvId: string) => {
    setCvLoading(true);
    setCvPage(0);
    setCvImages(null);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/cv/${cvId}/images`);
      if (resp.ok) {
        const data = await resp.json();
        setCvImages(data);
      }
    } catch (err) {
      console.error('CV load failed:', err);
    } finally {
      setCvLoading(false);
    }
  };

  // Keyboard navigation (arrow keys)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      const list = leftTab === 'saved' ? savedCandidates : candidates;
      if (!list.length) return;

      e.preventDefault();
      const currentId = selectedCandidate?.cv_id || selectedSpotlight?.cv_id;
      const currentIdx = list.findIndex(c => c.cv_id === currentId);
      let nextIdx: number;

      if (e.key === 'ArrowDown') {
        nextIdx = currentIdx < list.length - 1 ? currentIdx + 1 : 0;
      } else {
        nextIdx = currentIdx > 0 ? currentIdx - 1 : list.length - 1;
      }

      const next = list[nextIdx];
      if (next) {
        setSelectedCandidate(next);
        setSelectedSpotlight(null);
        loadCv(next.cv_id);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [candidates, savedCandidates, selectedCandidate, selectedSpotlight, leftTab]);

  // AI Generate
  const aiGenerate = async (cvId: string, mode: 'create' | 'edit' = 'create') => {
    if (!selectedCompany) return;
    setAiGenerating(true);
    try {
      const targetRole = mode === 'edit' ? editTargetRole : newTargetRole;
      const payload: any = { company_id: selectedCompany.id, cv_id: cvId, language: aiLanguage };
      if (targetRole.trim()) payload.target_role = targetRole.trim();
      if (jobDescription.trim()) payload.job_description = jobDescription.trim();
      const resp = await apiCall(`${API_BASE}/api/v1/spotlights/admin/ai-generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (resp.ok) {
        const data = await resp.json();
        if (mode === 'edit') {
          setEditSummary(data.summary || '');
          setEditFit(data.fit || '');
        } else {
          setNewSummary(data.summary || '');
          setNewFit(data.fit || '');
        }
        showToast('AI texts generated', 'success');
      } else {
        showToast('AI generation failed', 'error');
      }
    } catch (err) {
      showToast('AI generation failed', 'error');
    } finally {
      setAiGenerating(false);
    }
  };

  // Publish spotlight
  const publishSpotlight = async () => {
    if (!selectedCompany || !selectedCandidate || !newSummary.trim() || !newFit.trim()) return;
    try {
      const payload: any = {
        company_id: selectedCompany.id,
        cv_id: selectedCandidate.cv_id,
        candidate_user_id: selectedCandidate.explorer_id,
        summary: newSummary,
        fit: newFit,
      };
      if (newTargetRole.trim()) payload.target_role = newTargetRole.trim();
      const resp = await apiCall(`${API_BASE}/api/v1/spotlights/admin/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (resp.ok) {
        showToast('Spotlight published!', 'success');
        setNewTargetRole('');
        setNewSummary('');
        setNewFit('');
        setSelectedCandidate(null);
        loadSpotlights();
      }
    } catch (err) {
      showToast('Failed to publish', 'error');
    }
  };

  // Edit spotlight
  const saveEdit = async () => {
    if (!selectedSpotlight) return;
    try {
      const payload: any = { summary: editSummary, fit: editFit };
      if (editTargetRole.trim()) payload.target_role = editTargetRole.trim();
      const resp = await apiCall(`${API_BASE}/api/v1/spotlights/admin/${selectedSpotlight.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (resp.ok) {
        showToast('Spotlight updated', 'success');
        setSelectedSpotlight(null);
        loadSpotlights();
      }
    } catch (err) {
      showToast('Failed to update', 'error');
    }
  };

  // Delete spotlight
  const deleteSpotlight = async (id: string) => {
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/spotlights/admin/${id}`, { method: 'DELETE' });
      if (resp.ok) {
        showToast('Spotlight removed', 'success');
        loadSpotlights();
      }
    } catch (err) {
      showToast('Failed to remove', 'error');
    }
  };

  // Load stats
  const loadStats = async () => {
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/spotlights/admin/stats`);
      if (resp.ok) {
        const data = await resp.json();
        setStats(data.stats || []);
        setShowStats(true);
      }
    } catch (err) {
      console.error('Stats failed:', err);
    }
  };

  // Load saved CVs with enrichment
  const loadSavedCvs = useCallback(async () => {
    setSavedLoading(true);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/profiles/me/saved-cvs`);
      if (!resp.ok) return;
      const data = await resp.json();
      const cvIds = (data.saved_cvs || []).map((s: any) => s.cv_id);
      if (cvIds.length === 0) { setSavedCandidates([]); return; }
      const enrichResp = await apiCall(`${API_BASE}/api/v1/companies/saved-candidates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cv_ids: cvIds }),
      });
      if (enrichResp.ok) {
        const enrichData = await enrichResp.json();
        setSavedCandidates(enrichData.candidates || []);
      }
    } catch (err) {
      console.error('Saved CVs failed:', err);
    } finally {
      setSavedLoading(false);
    }
  }, [apiCall]);

  const statusStyle: Record<string, string> = {
    new: 'bg-pink-100 text-pink-600',
    seen: 'bg-slate-100 text-slate-500',
    saved: 'bg-amber-100 text-amber-600',
    reached_out: 'bg-emerald-100 text-emerald-600',
    dismissed: 'bg-red-100 text-red-500',
    queued: 'bg-blue-100 text-blue-600',
  };

  const visibleSpotlights = spotlights.filter(s => ['new', 'seen', 'saved'].includes(s.status));
  const queuedSpotlights = spotlights.filter(s => s.status === 'queued');
  const archivedSpotlights = spotlights.filter(s => ['dismissed', 'reached_out'].includes(s.status));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-pink-50/30">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between gap-4">
          <h1 className="text-lg font-bold text-slate-900 flex items-center gap-2 shrink-0">
            <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
            Spotlight Manager
          </h1>

          <div ref={companyPickerRef} className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={selectedCompany ? selectedCompany.name : companyQuery}
              onChange={(e) => {
                if (selectedCompany) {
                  setSelectedCompany(null);
                  setCompanyQuery(e.target.value);
                } else {
                  handleCompanyQueryChange(e.target.value);
                }
              }}
              onFocus={() => {
                if (selectedCompany) {
                  setCompanyQuery(selectedCompany.name);
                  setSelectedCompany(null);
                  searchCompanies(selectedCompany.name);
                } else if (companyQuery.trim()) {
                  setCompanyDropdownOpen(true);
                }
              }}
              placeholder="Company suchen..."
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none"
            />
            {selectedCompany && (
              <button
                onClick={() => { setSelectedCompany(null); setCompanyQuery(''); setCompanies([]); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100 text-slate-400"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            {companyDropdownOpen && companies.length > 0 && !selectedCompany && (
              <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-64 overflow-auto">
                {companies.map(c => (
                  <button
                    key={c.id}
                    onClick={() => {
                      setSelectedCompany(c);
                      setCompanyQuery('');
                      setCompanyDropdownOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-pink-50 transition-colors flex items-center gap-2"
                  >
                    <span className="text-sm font-medium text-slate-900">{c.name}</span>
                    {c.industry && <span className="text-[10px] text-slate-400">{c.industry}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-slate-500">
              {visibleSpotlights.length} aktiv · {queuedSpotlights.length} Reserve
            </span>
            <button onClick={loadStats} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500">
              <BarChart3 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {!selectedCompany ? (
        <div className="flex items-center justify-center py-32 text-slate-400">
          Company auswählen um zu starten
        </div>
      ) : (
        <div className="max-w-[1600px] mx-auto flex gap-4 p-4" style={{ height: 'calc(100vh - 60px)' }}>
          {/* LEFT: Candidate search / saved */}
          <div className="w-[360px] shrink-0 flex flex-col gap-3 overflow-hidden">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3">
              <div className="flex gap-1.5 mb-2">
                <button
                  onClick={() => setLeftTab('search')}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${
                    leftTab === 'search' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}
                >
                  <Search className="w-3 h-3" /> Suche
                </button>
                <button
                  onClick={() => { setLeftTab('saved'); loadSavedCvs(); }}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${
                    leftTab === 'saved' ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                  }`}
                >
                  <Bookmark className="w-3 h-3" /> Gespeichert
                  {savedCandidates.length > 0 && <span className={`text-[9px] px-1 py-0.5 rounded-full ${leftTab === 'saved' ? 'bg-white/20' : 'bg-amber-200/60'}`}>{savedCandidates.length}</span>}
                </button>
              </div>
              {leftTab === 'search' && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && discoverCandidates(searchQuery)}
                    placeholder="Rolle, Skills, Location..."
                    className="flex-1 border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-pink-500 outline-none"
                  />
                  <button
                    onClick={() => discoverCandidates(searchQuery)}
                    disabled={searchLoading}
                    className="bg-gradient-to-r from-pink-500 to-pink-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
                  >
                    <Search className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-auto space-y-2">
              {leftTab === 'saved' ? (
                savedLoading ? (
                  <div className="text-center py-10 text-slate-400 text-sm">Lädt...</div>
                ) : savedCandidates.length === 0 ? (
                  <div className="text-center py-10 text-slate-400 text-sm">Noch keine CVs gespeichert</div>
                ) : (
                  savedCandidates.map(c => {
                    const alreadyAdded = spotlights.some(s => s.cv_id === c.cv_id);
                    return (
                      <div
                        key={c.cv_id}
                        onClick={() => {
                          setSelectedCandidate(c);
                          setSelectedSpotlight(null);
                          loadCv(c.cv_id);
                          setNewTargetRole('');
                          setNewSummary('');
                          setNewFit('');
                        }}
                        className={`bg-white rounded-xl p-3 border cursor-pointer transition-all ${
                          selectedCandidate?.cv_id === c.cv_id
                            ? 'border-amber-400 bg-amber-50/30 shadow-sm'
                            : 'border-slate-200 hover:border-amber-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium text-slate-900 text-sm">{c.current_role || 'Unknown'}</p>
                          <Bookmark className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                        </div>
                        {c.base_locations && <p className="text-xs text-slate-500">📍 {c.base_locations}</p>}
                        {c.current_level && <p className="text-xs text-slate-500">{c.current_level}</p>}
                        {c.role_preferences && (
                          <p className="text-xs text-slate-400 mt-1 truncate">{c.role_preferences}</p>
                        )}
                        {c.preferred_locations && (
                          <p className="text-[11px] text-slate-400 truncate">🌍 {c.preferred_locations}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          {alreadyAdded ? (
                            <span className="text-[10px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full font-medium">
                              Bereits hinzugefügt
                            </span>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCandidate(c);
                                setSelectedSpotlight(null);
                                loadCv(c.cv_id);
                                aiGenerate(c.cv_id);
                              }}
                              className="flex items-center gap-1 text-[10px] font-medium text-pink-600 bg-pink-50 px-2 py-0.5 rounded-full hover:bg-pink-100"
                            >
                              <Star className="w-3 h-3" /> Zum Spotlight
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )
              ) : searchLoading ? (
                <div className="text-center py-10 text-slate-400 text-sm">Sucht...</div>
              ) : candidates.length === 0 ? (
                <div className="text-center py-10 text-slate-400 text-sm">Keine Ergebnisse</div>
              ) : (
                candidates.map(c => {
                  const alreadyAdded = spotlights.some(s => s.cv_id === c.cv_id);
                  return (
                    <div
                      key={c.cv_id}
                      onClick={() => {
                        setSelectedCandidate(c);
                        setSelectedSpotlight(null);
                        loadCv(c.cv_id);
                        setNewTargetRole('');
                        setNewSummary('');
                        setNewFit('');
                      }}
                      className={`bg-white rounded-xl p-3 border cursor-pointer transition-all ${
                        selectedCandidate?.cv_id === c.cv_id
                          ? 'border-pink-400 bg-pink-50/30 shadow-sm'
                          : 'border-slate-200 hover:border-pink-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium text-slate-900 text-sm">{c.current_role || 'Unknown'}</p>
                        <span className="text-[10px] text-slate-400">{(c.score * 100).toFixed(0)}%</span>
                      </div>
                      {c.base_locations && <p className="text-xs text-slate-500">📍 {c.base_locations}</p>}
                      {c.current_level && <p className="text-xs text-slate-500">{c.current_level}</p>}
                      {c.role_preferences && (
                        <p className="text-xs text-slate-400 mt-1 truncate">{c.role_preferences}</p>
                      )}
                      {c.preferred_locations && (
                        <p className="text-[11px] text-slate-400 truncate">🌍 {c.preferred_locations}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        {alreadyAdded ? (
                          <span className="text-[10px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full font-medium">
                            Bereits hinzugefügt
                          </span>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCandidate(c);
                              setSelectedSpotlight(null);
                              loadCv(c.cv_id);
                              aiGenerate(c.cv_id);
                            }}
                            className="flex items-center gap-1 text-[10px] font-medium text-pink-600 bg-pink-50 px-2 py-0.5 rounded-full hover:bg-pink-100"
                          >
                            <Star className="w-3 h-3" /> Zum Spotlight
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* CENTER: CV Viewer + Create/Edit panel */}
          <div ref={splitContainerRef} className="flex-1 flex flex-col overflow-hidden">
            {/* CV viewer */}
            <div style={{ height: `${cvSplitPercent}%` }} className="min-h-0 bg-white rounded-xl border border-slate-200 shadow-sm overflow-auto flex flex-col">
              {cvLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : cvImages && cvImages.images.length > 0 ? (
                <>
                  <div className="flex-1 overflow-auto flex justify-center p-4 bg-slate-50">
                    <img
                      src={`data:image/png;base64,${cvImages.images[cvPage]}`}
                      alt={`CV page ${cvPage + 1}`}
                      className="w-auto max-h-[calc(100vh-120px)] object-contain rounded-lg shadow"
                    />
                  </div>
                  {cvImages.images.length > 1 && (
                    <div className="flex items-center justify-center gap-3 py-2 border-t border-slate-200">
                      <button onClick={() => setCvPage(p => Math.max(0, p - 1))} disabled={cvPage === 0} className="p-1 rounded disabled:opacity-30">
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="text-xs text-slate-500">{cvPage + 1} / {cvImages.images.length}</span>
                      <button onClick={() => setCvPage(p => Math.min(cvImages.images.length - 1, p + 1))} disabled={cvPage === cvImages.images.length - 1} className="p-1 rounded disabled:opacity-30">
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
                  Kandidat:in auswählen um CV zu sehen
                </div>
              )}
            </div>

            {/* Drag handle */}
            {(selectedCandidate || selectedSpotlight) && (
              <div
                onMouseDown={handleDragStart}
                className="h-2 flex items-center justify-center cursor-row-resize group shrink-0"
              >
                <div className="w-12 h-1 rounded-full bg-slate-200 group-hover:bg-pink-300 transition-colors" />
              </div>
            )}

            {/* Create/Edit panel */}
            {selectedCandidate && !selectedSpotlight && (
              <div style={{ height: `${100 - cvSplitPercent - 2}%` }} className="min-h-0 bg-white rounded-xl border border-slate-200 shadow-sm p-4 overflow-y-auto">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-slate-900 flex items-center gap-1">
                    <Plus className="w-4 h-4" /> Zum Spotlight hinzufügen
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="flex rounded-lg border border-slate-300 overflow-hidden text-[10px] font-medium">
                      <button
                        onClick={() => setAiLanguage('de')}
                        className={`px-2 py-1 ${aiLanguage === 'de' ? 'bg-pink-500 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                      >DE</button>
                      <button
                        onClick={() => setAiLanguage('en')}
                        className={`px-2 py-1 ${aiLanguage === 'en' ? 'bg-pink-500 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                      >EN</button>
                    </div>
                    <button
                      onClick={() => aiGenerate(selectedCandidate.cv_id)}
                      disabled={aiGenerating}
                      className="flex items-center gap-1 text-xs font-medium bg-gradient-to-r from-pink-500 to-orange-400 text-white px-3 py-1.5 rounded-lg disabled:opacity-50"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      {aiGenerating ? 'Generating...' : 'AI Generate'}
                    </button>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Target Role *</label>
                    {jobSuggestions.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {jobSuggestions.map((jt, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setNewTargetRole(jt.title)}
                            className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                              newTargetRole === jt.title
                                ? 'bg-pink-50 border-pink-300 text-pink-700'
                                : 'bg-white border-slate-200 text-slate-600 hover:border-pink-200'
                            }`}
                          >
                            {jt.title}
                          </button>
                        ))}
                      </div>
                    )}
                    <input
                      type="text"
                      value={newTargetRole}
                      onChange={(e) => setNewTargetRole(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 outline-none"
                      placeholder="z.B. Backend Engineer, Werkstudent:in Marketing..."
                    />
                    <p className="text-[10px] text-slate-400 mt-0.5">AI nutzt die Role um Summary + Fit gezielt zu schreiben</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Job Description <span className="text-slate-400 font-normal">(optional)</span></label>
                    <textarea
                      value={jobDescription}
                      onChange={(e) => setJobDescription(e.target.value)}
                      rows={2}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-pink-500 outline-none resize-none"
                      placeholder="Stellenbeschreibung, Requirements, Tech-Stack..."
                    />
                    <p className="text-[10px] text-slate-400 mt-0.5">Hilft der AI bei genauerer Fit-Analyse</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Summary</label>
                    <textarea
                      value={newSummary}
                      onChange={(e) => setNewSummary(e.target.value)}
                      rows={2}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 outline-none resize-none"
                      placeholder="1-2 Sätze zum Profil..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Fit</label>
                    <textarea
                      value={newFit}
                      onChange={(e) => setNewFit(e.target.value)}
                      rows={2}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 outline-none resize-none"
                      placeholder="1-2 Sätze warum guter Fit..."
                    />
                  </div>
                  <button
                    onClick={publishSpotlight}
                    disabled={!newSummary.trim() || !newFit.trim()}
                    className="w-full flex items-center justify-center gap-1 bg-emerald-500 text-white font-medium py-2 rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-40 text-sm"
                  >
                    <Check className="w-4 h-4" /> Publish
                  </button>
                </div>
              </div>
            )}

            {/* Edit existing spotlight */}
            {selectedSpotlight && (
              <div style={{ height: `${100 - cvSplitPercent - 2}%` }} className="min-h-0 bg-white rounded-xl border border-slate-200 shadow-sm p-4 overflow-y-auto">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-slate-900 flex items-center gap-1">
                    <Edit3 className="w-4 h-4" /> Spotlight bearbeiten
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="flex rounded-lg border border-slate-300 overflow-hidden text-[10px] font-medium">
                      <button
                        onClick={() => setAiLanguage('de')}
                        className={`px-2 py-1 ${aiLanguage === 'de' ? 'bg-pink-500 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                      >DE</button>
                      <button
                        onClick={() => setAiLanguage('en')}
                        className={`px-2 py-1 ${aiLanguage === 'en' ? 'bg-pink-500 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                      >EN</button>
                    </div>
                    <button
                      onClick={() => aiGenerate(selectedSpotlight.cv_id, 'edit')}
                      disabled={aiGenerating}
                      className="flex items-center gap-1 text-xs font-medium bg-gradient-to-r from-pink-500 to-orange-400 text-white px-3 py-1.5 rounded-lg disabled:opacity-50"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      {aiGenerating ? '...' : 'AI Regenerate'}
                    </button>
                    <button onClick={() => setSelectedSpotlight(null)} className="text-slate-400 hover:text-slate-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Target Role</label>
                    {jobSuggestions.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {jobSuggestions.map((jt, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setEditTargetRole(jt.title)}
                            className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                              editTargetRole === jt.title
                                ? 'bg-pink-50 border-pink-300 text-pink-700'
                                : 'bg-white border-slate-200 text-slate-600 hover:border-pink-200'
                            }`}
                          >
                            {jt.title}
                          </button>
                        ))}
                      </div>
                    )}
                    <input
                      type="text"
                      value={editTargetRole}
                      onChange={(e) => setEditTargetRole(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 outline-none"
                      placeholder="z.B. Backend Engineer, Werkstudent:in Marketing..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Summary</label>
                    <textarea value={editSummary} onChange={(e) => setEditSummary(e.target.value)} rows={2}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 outline-none resize-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Fit</label>
                    <textarea value={editFit} onChange={(e) => setEditFit(e.target.value)} rows={2}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 outline-none resize-none" />
                  </div>
                  <button onClick={saveEdit} className="w-full bg-pink-500 text-white font-medium py-2 rounded-lg hover:bg-pink-600 text-sm">
                    Speichern
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: Active spotlights + Preview */}
          <div className="w-[320px] shrink-0 flex flex-col gap-3 overflow-hidden">
            {/* Preview */}
            {(selectedCandidate || selectedSpotlight) && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
                  <Eye className="w-3 h-3 inline mr-1" />So sieht's der Recruiter
                </p>
                <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-pink-100 text-pink-600">NEU</span>
                  <div className="mt-2 mb-2">
                    {(selectedSpotlight ? editTargetRole : newTargetRole) && (
                      <p className="text-[10px] font-medium text-pink-600 mb-0.5">🎯 {selectedSpotlight ? editTargetRole : newTargetRole}</p>
                    )}
                    <p className="font-semibold text-slate-900 text-sm">
                      {selectedCandidate?.current_role || selectedSpotlight?.profile?.current_role || 'Rolle'}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      📍 {selectedCandidate?.base_locations || selectedSpotlight?.profile?.base_locations || ''}
                    </p>
                    {(selectedCandidate?.current_level || selectedSpotlight?.profile?.current_level) && (
                      <p className="text-xs text-slate-500">{selectedCandidate?.current_level || selectedSpotlight?.profile?.current_level}</p>
                    )}
                  </div>
                  {(() => {
                    const text = selectedSpotlight ? editSummary : newSummary || '...';
                    const bullets = text.includes('\n') ? text.split('\n').filter(Boolean) : [text];
                    return (
                      <ul className="space-y-1 mb-2">
                        {bullets.map((b, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-sm text-slate-700">
                            {bullets.length > 1 && <span className="text-pink-400 mt-1 text-[8px] shrink-0">●</span>}
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    );
                  })()}
                  {(selectedCandidate?.role_preferences || selectedSpotlight?.profile?.role_preferences || selectedCandidate?.preferred_locations || selectedSpotlight?.profile?.preferred_locations) && (
                    <div className="pl-3 border-l-2 border-pink-300 mb-3">
                      {(selectedCandidate?.role_preferences || selectedSpotlight?.profile?.role_preferences) && (
                        <p className="text-xs text-slate-500">
                          <span className="text-pink-600 font-medium">Sucht:</span> {selectedCandidate?.role_preferences || selectedSpotlight?.profile?.role_preferences}
                        </p>
                      )}
                      {(selectedCandidate?.preferred_locations || selectedSpotlight?.profile?.preferred_locations) && (
                        <p className="text-[11px] text-slate-400 flex items-center gap-0.5 mt-0.5">
                          🌍 {selectedCandidate?.preferred_locations || selectedSpotlight?.profile?.preferred_locations}
                        </p>
                      )}
                    </div>
                  )}
                  <div className="pl-3 border-l-2 border-slate-700 mb-3">
                    <p className="text-xs font-medium text-slate-800 mb-1">Fit:</p>
                    {(() => {
                      const text = selectedSpotlight ? editFit : newFit || '...';
                      const bullets = text.includes('\n') ? text.split('\n').filter(Boolean) : [text];
                      return (
                        <ul className="space-y-1">
                          {bullets.map((b, i) => (
                            <li key={i} className="flex items-start gap-1.5 text-sm text-slate-600">
                              {bullets.length > 1 && <span className="text-slate-400 mt-1 text-[8px] shrink-0">●</span>}
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    })()}
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                    <span className="text-[10px] text-slate-400 flex items-center gap-1"><FileText className="w-3 h-3" /> CV ansehen</span>
                    <span className="text-[10px] text-white bg-pink-500 px-2 py-0.5 rounded font-medium">Anschreiben</span>
                  </div>
                </div>
              </div>
            )}

            {/* Active spotlights list */}
            <div className="flex-1 overflow-auto">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2 px-1">
                Aktive Spotlight ({visibleSpotlights.length})
              </p>
              <div className="space-y-2">
                {visibleSpotlights.map(s => (
                  <div
                    key={s.id}
                    onClick={() => {
                      setSelectedSpotlight(s);
                      setSelectedCandidate(null);
                      setEditTargetRole(s.target_role || '');
                      setEditSummary(s.summary);
                      setEditFit(s.fit);
                      loadCv(s.cv_id);
                    }}
                    className={`bg-white rounded-lg p-2.5 border cursor-pointer transition-all ${
                      selectedSpotlight?.id === s.id ? 'border-pink-400 bg-pink-50/30' : 'border-slate-200 hover:border-pink-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium text-slate-900 truncate">
                        {s.profile?.current_role || s.cv_id.slice(0, 8)}
                      </p>
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${statusStyle[s.status]}`}>
                        {s.status}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-0.5 truncate">{s.summary}</p>
                    <div className="flex gap-1 mt-1.5">
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedSpotlight(s); setEditTargetRole(s.target_role || ''); setEditSummary(s.summary); setEditFit(s.fit); }}
                        className="text-[10px] text-slate-500 hover:text-pink-600 flex items-center gap-0.5"
                      >
                        <Edit3 className="w-3 h-3" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteSpotlight(s.id); }}
                        className="text-[10px] text-slate-400 hover:text-red-500 flex items-center gap-0.5"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {queuedSpotlights.length > 0 && (
                <>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mt-4 mb-2 px-1">
                    Reserve ({queuedSpotlights.length})
                  </p>
                  <div className="space-y-2">
                    {queuedSpotlights.map(s => (
                      <div key={s.id} className="bg-white rounded-lg p-2.5 border border-dashed border-slate-300">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-medium text-slate-700 truncate">
                            {s.profile?.current_role || s.cv_id.slice(0, 8)}
                          </p>
                          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600">queued</span>
                        </div>
                        <div className="flex gap-1 mt-1.5">
                          <button
                            onClick={() => { setSelectedSpotlight(s); setEditTargetRole(s.target_role || ''); setEditSummary(s.summary); setEditFit(s.fit); setSelectedCandidate(null); loadCv(s.cv_id); }}
                            className="text-[10px] text-slate-500 hover:text-pink-600 flex items-center gap-0.5"
                          >
                            <Edit3 className="w-3 h-3" /> Edit
                          </button>
                          <button
                            onClick={() => deleteSpotlight(s.id)}
                            className="text-[10px] text-slate-400 hover:text-red-500 flex items-center gap-0.5"
                          >
                            <Trash2 className="w-3 h-3" /> Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {archivedSpotlights.length > 0 && (
                <>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mt-4 mb-2 px-1">
                    Archiv ({archivedSpotlights.length})
                  </p>
                  <div className="space-y-2">
                    {archivedSpotlights.map(s => (
                      <div key={s.id} className="bg-white rounded-lg p-2.5 border border-slate-200 opacity-60">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-medium text-slate-700 truncate">
                            {s.profile?.current_role || s.cv_id.slice(0, 8)}
                          </p>
                          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${statusStyle[s.status]}`}>
                            {s.status}
                          </span>
                        </div>
                        <p className="text-[10px] text-slate-500 mt-0.5 truncate">{s.summary}</p>
                        <div className="flex gap-1 mt-1.5">
                          <button
                            onClick={() => deleteSpotlight(s.id)}
                            className="text-[10px] text-slate-400 hover:text-red-500 flex items-center gap-0.5"
                          >
                            <Trash2 className="w-3 h-3" /> Löschen
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stats Modal */}
      {showStats && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowStats(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-auto p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Spotlight Stats</h2>
              <button onClick={() => setShowStats(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-slate-500">
                  <th className="pb-2">Company</th>
                  <th className="pb-2 text-center">Total</th>
                  <th className="pb-2 text-center">Neu</th>
                  <th className="pb-2 text-center">Gesehen</th>
                  <th className="pb-2 text-center">Gespeichert</th>
                  <th className="pb-2 text-center">Kontaktiert</th>
                  <th className="pb-2 text-center">Reserve</th>
                </tr>
              </thead>
              <tbody>
                {stats.map(s => (
                  <tr key={s.company_id} className="border-b border-slate-100">
                    <td className="py-2 font-medium">{s.company_name}</td>
                    <td className="py-2 text-center">{s.total}</td>
                    <td className="py-2 text-center text-pink-600">{s.new}</td>
                    <td className="py-2 text-center">{s.seen}</td>
                    <td className="py-2 text-center text-amber-600">{s.saved}</td>
                    <td className="py-2 text-center text-emerald-600">{s.reached_out}</td>
                    <td className="py-2 text-center text-blue-600">{s.queued}</td>
                  </tr>
                ))}
                {stats.length === 0 && (
                  <tr><td colSpan={7} className="py-8 text-center text-slate-400">Noch keine Spotlights erstellt</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] px-4 py-2.5 rounded-xl shadow-lg text-sm font-medium ${
          toast.type === 'success' ? 'bg-emerald-500 text-white' : toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-slate-800 text-white'
        }`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default SpotlightManager;
